import React, { useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Alert,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createPack } from "../../services/apiServices";

const AddPack = ({ onSuccess, onCancel }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState({ status: null, message: "" });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      packAmount: "",
      creator: "",
      status: "active",
      image: null,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Pack name is required"),
      packAmount: Yup.number()
        .typeError("Pack amount must be a number")
        .required("Pack amount is required")
        .min(0, "Pack amount cannot be negative"),
      creator: Yup.string().required("Creator is required"),
      status: Yup.string().required("Status is required"),
      image: Yup.mixed().required("Pack image is required"),
    }),
    onSubmit: async (values) => {
      console.log("values", values);
      try {
        setSubmitting(true);
        setApiResponse({ status: null, message: "" });

        // Prepare FormData for file upload
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("packAmount", values.packAmount);
        formData.append("creator", values.creator);
        // formData.append("status", values.status);
        formData.append("file", values.image);

        await createPack(formData);
        setSubmitting(false);
        // headers handled in apiServices
        return;
        setApiResponse({
          status: "success",
          message: "Pack created successfully!",
        });
        validation.resetForm();

        if (onSuccess) setTimeout(() => onSuccess(), 1500);
      } catch (error) {
        setApiResponse({
          status: "error",
          message: error.response?.data?.message || "Failed to create pack.",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    if (onCancel) onCancel();
    else if (onSuccess) onSuccess();
  };

  return (
    <div className="page-content d-flex justify-content-center min-vh-100">
      <Container fluid className="mw-50 w-50">
        {apiResponse.status && (
          <Alert
            color={apiResponse.status === "success" ? "success" : "danger"}
            className="mb-4">
            {apiResponse.message}
          </Alert>
        )}

        <Form
          onSubmit={validation.handleSubmit}
          className="p-4 border rounded bg-light">
          <h4 className="text-center mb-4">Create New Pack</h4>

          {/* Name */}
          <Row>
            <Col>
              <Label htmlFor="name">Pack Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Enter pack name"
                value={validation.values.name}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={!!(validation.touched.name && validation.errors.name)}
                disabled={submitting}
              />
              {validation.touched.name && validation.errors.name && (
                <div className="text-danger">{validation.errors.name}</div>
              )}
            </Col>
          </Row>

          {/* Pack Amount */}
          <Row className="mt-3">
            <Col>
              <Label htmlFor="packAmount">Pack Amount</Label>
              <Input
                id="packAmount"
                name="packAmount"
                type="number"
                placeholder="Enter pack amount"
                value={validation.values.packAmount}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(
                    validation.touched.packAmount &&
                    validation.errors.packAmount
                  )
                }
                disabled={submitting}
              />
              {validation.touched.packAmount &&
                validation.errors.packAmount && (
                  <div className="text-danger">
                    {validation.errors.packAmount}
                  </div>
                )}
            </Col>
          </Row>

          {/* Creator */}
          <Row className="mt-3">
            <Col>
              <Label htmlFor="creator">Creator</Label>
              <Input
                id="creator"
                name="creator"
                type="text"
                placeholder="Enter creator name"
                value={validation.values.creator}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.creator && validation.errors.creator)
                }
                disabled={submitting}
              />
              {validation.touched.creator && validation.errors.creator && (
                <div className="text-danger">{validation.errors.creator}</div>
              )}
            </Col>
          </Row>

          {/* Status */}
          <Row className="mt-3">
            <Col>
              <Label htmlFor="status">Status</Label>
              <Input
                id="status"
                name="status"
                type="select"
                value={validation.values.status}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                disabled={submitting}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Input>
            </Col>
          </Row>

          {/* Image */}
          <Row className="mt-3">
            <Col>
              <Label htmlFor="image">Pack Image</Label>
              <Input
                id="image"
                name="image"
                type="file"
                onChange={(e) =>
                  validation.setFieldValue("image", e.target.files[0])
                }
                disabled={submitting}
              />
              {validation.touched.image && validation.errors.image && (
                <div className="text-danger">{validation.errors.image}</div>
              )}
            </Col>
          </Row>

          {/* Actions */}
          <div className="d-flex justify-content-between mt-4">
            <Button
              color="secondary"
              onClick={handleCancel}
              disabled={submitting}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                "Create Pack"
              )}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  );
};

export default AddPack;
